export type Observer<T> = (arg: T) => void;

export interface Subscription {
    unsubscribe(): void;
}

export class Observable<T> {

    private readonly subscribe_: (observer: Observer<T>) => Subscription;

    public constructor(subscribe: (observer: Observer<T>) => Subscription) {
        this.subscribe_ = subscribe;
    }

    public debounce(wait: number): Observable<T> {
        return new Observable<T>(observer => {
            let timeout;
            return this.subscribe(arg => {
                const later = () => {
                    timeout = null;
                    observer(arg);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            });
        });
    }

    public filter(fn: (arg: T) => boolean): Observable<T> {
        return new Observable<T>(observer => {
            return this.subscribe(arg => {
                if (fn(arg)) {
                    observer(arg);
                }
            });
        });
    }

    public map<U>(fn: (arg: T) => U): Observable<U> {
        return new Observable<U>(observer => {
            return this.subscribe(arg => {
                observer(fn(arg));
            });
        });
    }

    public subscribe(observer: Observer<T>): Subscription {
        return this.subscribe_(observer);
    }
}

export class Signal<T> extends Observable<T> {

    private observers_?: Observer<T>[];

    public constructor() {
        super(observer => {
            if (!this.observers_) {
                this.observers_ = [observer];
            }
            else if (this.observers_.indexOf(observer) < 0) {
                this.observers_.push(observer);
            }
            const observers = this.observers_;
            return {
                unsubscribe: () => {
                    const index = observers.indexOf(observer);
                    if (index >= 0) {
                        observers.splice(index, 1);
                    }
                }
            };
        });
        this.observers_ = [];
    }

    public dispatch(arg: T) {
        if (this.observers_) {
            for (const observer of this.observers_) {
                observer(arg);
            }
        }
    }
}

/**
 * Listens to an event target event and creates an observable instance
 * @param el the event target tzo listen to
 * @param event the name of the event to listen to
 */
export function observable<TEvent extends Event>( event:string, el:EventTarget ):Observable<TEvent> {
	return new Observable( observer => {
		let handler = ( event ) => observer( event );
		el.addEventListener( event, handler );
		return {
			unsubscribe:() => {
				el.removeEventListener( event, handler );
			}
		}
	});
}
